"use client";
import React from "react";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";

interface ContentTextProps {
    title: string;
    text: string;
}

interface Button {
    id: string;
    url: string;
    text: string;
    type: string;
    newTab: boolean;
}

interface Picture {
    data: {
        id: string;
        attributes: {
            url: string;
            name: string;
            alternativeText: string;
        };
    };
}

interface ContentProps {
    data: {
        id: number;
        title: string;
        subtitle: string;
        picture: Picture;
        isReversed: boolean;
        contentTexts: ContentTextProps[];
        button: Button[];
        isLive: boolean;
        useH1Tag?: boolean;
        variant?: "home" | "patent";
        bgColor?: string;
    };
}

export default function Content_Home_Layout5({ data }: ContentProps) {
    const pathname = usePathname();
    const imgUrl = getStrapiMedia(data.picture.data.attributes.url);

    return (
        <section className="relative w-full min-h-screen bg-[#FFFCF7] overflow-hidden py-16 px-6">
            {/* Decorative Layers */}
            <div className="absolute inset-0 bg-[url('/assets/luxury-texture.png')] bg-cover opacity-10 z-0" />
            <div className="absolute inset-0 z-0 pointer-events-none">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#D4A76A] to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-transparent via-[#D4A76A] to-transparent"></div>
            </div>

            {/* Container */}
            <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 items-center gap-12">
                {/* Image Column */}
                <div className="relative h-[400px] sm:h-[500px] w-full rounded-3xl shadow-xl overflow-hidden border-[6px] border-white">
                    <Image
                        src={imgUrl || "/placeholder.jpg"}
                        alt={data.picture?.data?.attributes?.alternativeText || "Premium herbal product"}
                        fill
                        className="object-cover object-center transition-transform duration-700 hover:scale-105"
                        sizes="(max-width: 768px) 100vw, 50vw"
                        priority
                    />
                    <div className="absolute inset-0 bg-[#F5E9D9]/20 backdrop-blur-[2px] mix-blend-multiply"></div>
                </div>

                {/* Text Column */}
                <div className="relative z-10 flex flex-col gap-6 text-center lg:text-left">
                    <div className="inline-flex items-center gap-2 justify-center lg:justify-start text-[#B78D65] uppercase text-sm tracking-widest font-semibold">
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                        </svg>
                        Certified Organic
                    </div>

                    <h1 className="text-[#2A2118] text-4xl md:text-5xl xl:text-6xl   italic leading-tight">
                        <span className="block text-[#A67C52] font-medium">Revitalize</span>
                        Your Natural Beauty
                    </h1>

                    <p className="text-[#5A4A3A]/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0">
                        Our artisan-crafted herbal blends enhance your hair's natural radiance with 100% organic ingredients.
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4 text-left">
                        {[
                            "Chemical-free formulations",
                            "Hand-harvested herbs",
                            "Sustainable sourcing",
                            "Proven results",
                        ].map((item, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                                <span className="w-6 h-6 rounded-full bg-[#F5E9D9] text-[#B78D65] flex items-center justify-center">
                                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </span>
                                <span className="text-sm sm:text-base text-[#5A4A3A]">{item}</span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-4">
                        <button className="relative group inline-flex items-center gap-2 px-8 py-3 rounded-full text-white bg-gradient-to-br from-[#B78D65] to-[#A67C52] font-medium text-base sm:text-lg shadow-md hover:shadow-lg transition-all duration-300">
                            Discover Our Blends
                            <svg
                                className="w-5 h-5 transition-transform group-hover:translate-x-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
